import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';

// Helper to optimize images using Cloudflare resizing on the fly
const getResizedImageUrl = (url, width = 800, quality = 80) => {
  if (!url || !url.startsWith('https://assets.auxodev.dk/')) return url;
  if (url.includes('/cdn-cgi/image/')) return url;
  return url.replace('https://assets.auxodev.dk/', `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=${quality},format=auto/`);
};

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/projects');
        const data = await response.json();
        // Sort projects to show the newest or most relevant first
        setProjects(data);
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (selectedProject) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [selectedProject]);

  // Preload adjacent images in the background for instant switching
  useEffect(() => {
    if (!selectedProject || !selectedProject.image || selectedProject.image.length <= 1) return;

    const preloadIndices = [
      (currentImageIndex + 1) % selectedProject.image.length,
      (currentImageIndex - 1 + selectedProject.image.length) % selectedProject.image.length
    ];

    preloadIndices.forEach((idx) => {
      const imgUrl = getResizedImageUrl(selectedProject.image[idx], 1200);
      const img = new Image();
      img.src = imgUrl;
    });
  }, [selectedProject, currentImageIndex]);

  // Prefetch first image of a project on hover
  const handleProjectHover = (project) => {
    if (!project || !project.image || project.image.length === 0) return;
    const imgUrl = getResizedImageUrl(project.image[0], 1200);
    const img = new Image();
    img.src = imgUrl;
  };

  // Keyboard navigation for modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedProject) return;

      if (e.key === 'Escape') {
        setSelectedProject(null);
      } else if (e.key === 'ArrowLeft') {
        setCurrentImageIndex((prev) =>
          prev === 0 ? selectedProject.image.length - 1 : prev - 1
        );
      } else if (e.key === 'ArrowRight') {
        setCurrentImageIndex((prev) =>
          prev === selectedProject.image.length - 1 ? 0 : prev + 1
        );
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedProject]);

  const handleOpenProject = (project) => {
    setSelectedProject(project);
    setCurrentImageIndex(0);
  };

  const handleCloseModal = () => {
    setSelectedProject(null);
    setCurrentImageIndex(0);
  };

  const handlePrevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) =>
      prev === 0 ? selectedProject.image.length - 1 : prev - 1
    );
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) =>
      prev === selectedProject.image.length - 1 ? 0 : prev + 1
    );
  };

  if (loading) return (
    <div className="py-16 text-center bg-slate-900">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500 mx-auto"></div>
      <p className="mt-4 text-slate-400">Indlæser projekter...</p>
    </div>
  );

  return (
    <section id="projekter" className="py-20 bg-slate-900 text-white relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-8">
          <div className="max-w-2xl">
            <span className="text-orange-500 text-xs font-bold uppercase tracking-widest mb-3 block">
              Vores Referencer
            </span>
            <h3 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 tracking-tight">
              Udvalgte projekter og referencer
            </h3>
            <p className="text-slate-300 text-lg leading-relaxed">
              Se et udpluk af de opgaver vi har løst for vores kunder. Vi lægger vægt på holdbare løsninger, præcision og æstetisk finish. Klik på et projekt for at se flere billeder.
            </p>
          </div>
          <a
            href="https://www.facebook.com/p/SL-Flise-og-murerforretning-100049126461160/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center bg-slate-800/80 hover:bg-slate-800 text-white px-6 py-3 rounded-xl font-bold transition-all border border-slate-700 hover:border-orange-500/50 group shrink-0"
          >
            Se flere på Facebook
            <ExternalLink className="ml-2 h-5 w-5 text-orange-500 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </a>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.slice(0, 3).map((project) => (
            <div
              key={project.id}
              onClick={() => handleOpenProject(project)}
              onMouseEnter={() => handleProjectHover(project)}
              className="group relative overflow-hidden rounded-2xl aspect-[4/5] bg-slate-950 border border-slate-800/50 cursor-pointer shadow-lg hover:shadow-orange-500/5 transition-all duration-500"
            >
              {/* Project Image */}
              <img
                src={getResizedImageUrl(project.image[0], 600)}
                alt={project.title.da}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              
              {/* Image Count / Zoom Badge */}
              <div className="absolute top-4 right-4 bg-slate-950/80 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-2 border border-slate-800/80 shadow-md">
                <i className="fa-solid fa-images text-orange-500"></i>
                <span>{project.image ? project.image.length : 1} billeder</span>
              </div>

              {/* Project Info - Simple & Clean */}
              <div className="absolute bottom-0 left-0 p-4 w-full bg-gradient-to-t from-slate-950/90 to-transparent">
                <h4 className="text-base font-semibold text-white">
                  {project.title.da}
                </h4>
              </div>
            </div>
          ))}
        </div>

        {/* View All Projects Button */}
        <div className="mt-12 text-center">
          <Link
            to="/projekter"
            className="inline-flex items-center bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-xl font-bold transition-all shadow-lg shadow-orange-500/10 hover:shadow-orange-500/20 group cursor-pointer"
          >
            Se alle projekter
            <i className="fa-solid fa-arrow-right ml-2 group-hover:translate-x-1 transition-transform"></i>
          </Link>
        </div>
      </div>

      {/* Project Detail Modal */}
      {selectedProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 md:p-10 bg-slate-950/95 backdrop-blur-md overflow-y-auto">
          <div 
            className="relative max-w-4xl w-full bg-slate-900 rounded-2xl overflow-hidden border border-slate-800 shadow-2xl flex flex-col my-8"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 z-20 bg-slate-950/80 hover:bg-orange-500 text-white hover:text-white p-3 rounded-full transition-all border border-slate-800 focus:outline-none"
              aria-label="Luk"
            >
              <i className="fa-solid fa-xmark text-lg"></i>
            </button>

            {/* Gallery Viewer */}
            <div className="w-full bg-slate-950 flex flex-col justify-center relative group/viewer">
              {/* Main Active Image */}
              <div 
                className="relative aspect-[4/3] sm:aspect-[16/10] md:aspect-[16/9] flex items-center justify-center overflow-hidden"
              >
                <img
                  src={getResizedImageUrl(selectedProject.image[currentImageIndex], 1200)}
                  alt={`${selectedProject.title.da} - ${currentImageIndex + 1}`}
                  className="max-w-full max-h-[65vh] object-contain transition-all duration-300"
                />

                {/* Navigation Arrows on Main Image */}
                {selectedProject.image.length > 1 && (
                  <>
                    <button
                      onClick={handlePrevImage}
                      className="absolute left-4 bg-slate-900/80 hover:bg-orange-500 text-white p-3 rounded-full transition-all border border-slate-800 opacity-0 group-hover/viewer:opacity-100 focus:outline-none"
                    >
                      <i className="fa-solid fa-chevron-left"></i>
                    </button>
                    <button
                      onClick={handleNextImage}
                      className="absolute right-4 bg-slate-900/80 hover:bg-orange-500 text-white p-3 rounded-full transition-all border border-slate-800 opacity-0 group-hover/viewer:opacity-100 focus:outline-none"
                    >
                      <i className="fa-solid fa-chevron-right"></i>
                    </button>
                  </>
                )}
              </div>

              {/* Thumbnails Grid */}
              {selectedProject.image.length > 1 && (
                <div className="p-4 bg-slate-900/50 border-t border-slate-800/80 overflow-x-auto">
                  <div className="flex gap-2 justify-start sm:justify-center min-w-max">
                    {selectedProject.image.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentImageIndex(idx)}
                        className={`relative w-16 h-16 rounded-lg overflow-hidden border-2 transition-all focus:outline-none shrink-0 ${
                          idx === currentImageIndex 
                            ? 'border-orange-500 scale-105 shadow-md shadow-orange-500/20' 
                            : 'border-slate-800 hover:border-slate-600'
                        }`}
                      >
                        <img
                          src={getResizedImageUrl(img, 150)}
                          alt={`Miniature ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Bottom Bar: Project Info & Actions */}
            <div className="p-6 bg-slate-900 border-t border-slate-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-white tracking-tight">
                  {selectedProject.title.da}
                </h3>
                <p className="text-slate-400 text-xs mt-1">
                  Billede {currentImageIndex + 1} af {selectedProject.image.length}
                </p>
              </div>

              <div className="flex items-center gap-3">
                <a
                  href="#kontakt"
                  onClick={() => {
                    handleCloseModal();
                    setTimeout(() => {
                      const contactSection = document.getElementById('kontakt');
                      if (contactSection) {
                        contactSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }, 100);
                  }}
                  className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-2.5 px-5 rounded-xl transition-all text-sm flex items-center gap-2 shadow-lg shadow-orange-500/10"
                >
                  <i className="fa-solid fa-paper-plane"></i>
                  <span>Få et lignende tilbud</span>
                </a>
                <button
                  onClick={handleCloseModal}
                  className="bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold py-2.5 px-4 rounded-xl transition-all text-sm border border-slate-700"
                >
                  Luk
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Projects;
